import { Dispatch, SetStateAction } from "react";
import { ITaskFormDisplay } from "../../common/api_interfaces/task/ITaskFormDisplay";

// #region Валидатор

export class Validator {
    /**
     * Создает универсальную функцию валидации для различных полей
     * 
     * @param condition - Условие, при котором возникает ошибка
     * @param errorMessage - Сообщение об ошибке
     * @param setError - Функция установки ошибки
     * @returns {boolean} true - если есть ошибка, false - если ошибок нет
     */
    private static createValidator = (
        condition: boolean,
        errorMessage: string,
        setError: Dispatch<SetStateAction<string | undefined>>
    ): boolean => {
        const hasError = condition;
        setError(hasError ? errorMessage : undefined);
        return hasError;
    }

    /**
     * Валидирует имя задачи и устанавливает сообщение об ошибке
     * 
     * @param name - Имя задачи для валидации
     * @param setName - Функция для установки сообщения об ошибки
     * @returns {boolean} true - если есть ошибка валидации, false - если ошибок нет
     * 
     * Функция проверяет, что имя задачи не является пустой строкой или строкой,
     * состоящей только из пробельных символов. В случае ошибки устанавливает
     * соответствующее сообщение через функцию setName.
     */
    static validateName = (
        name: string,
        setName: Dispatch<SetStateAction<string | undefined>>
    ): boolean => {
        return this.createValidator(
            !name.trim().length,
            'Заполните имя задачи',
            setName
        );
    };

    static validateDesc = (
        maxDescription: boolean,
        setDescError: Dispatch<SetStateAction<string | undefined>>
    ): boolean => {
        return this.createValidator(
            maxDescription,
            'Текст превысил допустимую длину',
            setDescError
        );
    }

    /**
     * Валидирует корректность временного интервала (дата начала и окончания)
     * 
     * @param startDate - Дата начала задачи (может быть null или undefined)
     * @param endDate - Дата окончания задачи (может быть null или undefined)
     * @param setDatesError - Функция для установки сообщения об ошибке
     * @returns {boolean} true - если есть ошибка валидации, false - если ошибок нет
     * 
     * Функция проверяет, что дата окончания не раньше даты начала.
     * Если обе даты указаны и endDate < startDate, устанавливается ошибка.
     */
    static validateDates = (
        startDate: Date | null | undefined,
        endDate: Date | null | undefined,
        setDatesError: Dispatch<SetStateAction<string | undefined>>
    ): boolean => {
        return this.createValidator(
            !!(startDate && endDate && endDate < startDate),
            "Дата окончания не может быть раньше даты начала",
            setDatesError
        );
    };

    /**
     * Валидирует поле ответственного
     * 
     * @param responsible - Выбранный ответственный (может быть null/undefined)
     * @param responsibleRequired - Флаг обязательности поля
     * @param setResponsibleError - Функция установки ошибки
     * @returns {boolean} true - если есть ошибка, false - если ошибок нет
     */
    static validateResponsible = (
        responsible: any | null | undefined,
        responsibleRequired: boolean,
        setResponsibleError: Dispatch<SetStateAction<string | undefined>>
    ): boolean => {
        if (!responsibleRequired) {
            setResponsibleError('');
            return false;
        }

        return this.createValidator(
            !responsible,
            "Заполните ответственного в задаче",
            setResponsibleError
        );
    };

    /**
     * Валидирует поле типа задачи
     * 
     * @param type - Выбранный тип задачи (может быть null/undefined)
     * @param setTypeError - Функция установки ошибки
     * @returns {boolean} true - если есть ошибка, false - если ошибок нет
     */
    static validateType = (
        type: any | null | undefined,
        setTypeError: Dispatch<SetStateAction<string | undefined>>
    ): boolean => {
        return this.createValidator(
            !type,
            "Выберите тип задачи",
            setTypeError
        );
    };

    /**
     * Валидирует поле приоритета задачи
     * 
     * @param priority - Выбранный приоритет (может быть null/undefined)
     * @param setPriorityError - Функция установки ошибки
     * @returns {boolean} true - если есть ошибка, false - если ошибок нет
     */
    static validatePriority = (
        priority: any | null | undefined,
        setPriorityError: Dispatch<SetStateAction<string | undefined>>
    ): boolean => {
        return this.createValidator(
            !priority,
            "Выберите приоритет задачи",
            setPriorityError
        );
    };

    /**
     * Валидирует наличие доски для создания задачи
     * 
     * @param columnId - ID колонки (может быть null/undefined)
     * @param boardId - ID доски (может быть null/undefined)
     * @param taskData - Данные задачи (может быть null/undefined)
     * @param setBoardError - Функция установки ошибки
     * @returns {boolean} true - если есть ошибка, false - если ошибок нет
     */
    static validateBoard = (
        columnId: number | null | undefined,
        boardId: number | null | undefined,
        taskData: ITaskFormDisplay | null | undefined,
        setBoardError: Dispatch<SetStateAction<string | undefined>>
    ): boolean => {
        return this.createValidator(
            !columnId && !boardId && !taskData,
            "Выберите доску для создания",
            setBoardError
        );
    };
}
// #endregion